import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useLocation } from "react-router-dom";
import ThreeStepTimeline from "./ThreeStepTimeline";

export default function AutoTimelineSequence({
  steps,
  onComplete,
  duration = 4,
  finalButtonText = "Next Section",
}) {
  const [stepIndex, setStepIndex] = useState(0);
  const [showFinalButton, setShowFinalButton] = useState(false);
  const location = useLocation();

  const currentStep = steps[stepIndex];
  const isFinal = currentStep?.final;

  // Check if current route is /learner/journey
  const isJourneyPage = location.pathname === "/learner/journey";

  /* ---------------- Normalize circles ---------------- */
  function normalizeCircles(circlesObj) {
    if (!circlesObj || typeof circlesObj !== "object") {
      return [
        { position: "left", label: "" },
        { position: "center", label: "" },
        { position: "right", label: "" },
      ];
    }

    return [
      { position: "left", label: circlesObj.left || "" },
      { position: "center", label: circlesObj.center || "" },
      { position: "right", label: circlesObj.right || "" },
    ];
  }

  const circlesArray = normalizeCircles(currentStep?.circles);

  /* ---------------- Auto-switch steps ---------------- */
  useEffect(() => {
    if (!currentStep) return;

    if (currentStep.final) {
      setTimeout(() => setShowFinalButton(true), 900);
      return;
    }

    const timeout = setTimeout(() => {
      setStepIndex((prev) => prev + 1);
    }, currentStep.duration || duration * 1000);

    return () => clearTimeout(timeout);
  }, [stepIndex, currentStep, duration]);

  /* ---------------- Horizontal positions ---------------- */
  const CARD_POS = {
    left: "left-[12%]",
    center: "left-[40%]",
    right: "left-[75%]",
  };

  const StepComponent = currentStep?.component;

  if (!currentStep || !StepComponent) {
    return null;
  }

  return (
    <div className="relative w-full h-[100vh] min-h-screen overflow-y-scroll scrollbar-hide bg-white flex flex-col justify-center">

      {/* Header Section - Only show on /learner/journey */}
      {isJourneyPage && (
        <div className="w-full mt-[2%] ml-[5%] 2xl:ml-[7%] absolute top-6">
          <h1 className="font-gotham font-medium text-[26px] 2xl:text-[36px] text-black leading-tight mb-1.5 xl:mb-2">
            Your Journey So Far
          </h1>
          <p className="font-gotham font-normal text-base xl:text-[20px] text-gray-700 max-w-3xl">
            A walkthrough of the key tasks you've completed and how each step moved your career forward.
          </p>
        </div>
      )}

      {/* Timeline */}
      <div className="w-full absolute top-[45%] 2xl:top-[38%]">
        <ThreeStepTimeline circles={circlesArray} />
      </div>

      {/* Card */}
      <motion.div
        key={stepIndex}
        initial={{ opacity: 0, x: 200 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 1.8 }}
        className={`
          absolute
          ${CARD_POS[currentStep.position]}
          z-20
          top-[18%] 2xl:top-[16%]
          ${isFinal ? "translate-y-0" : "-translate-y-1/2"}
          transform-gpu
        `}
      >
        <div className="relative">
          <StepComponent {...(currentStep.props || {})} />
        </div>
      </motion.div>

      {/* Final CTA */}
      {showFinalButton && isFinal && (
        <div className="absolute bottom-4 2xl:bottom-1 left-1/2 -translate-x-1/2 2xl:translate-x-[-22%] z-30">
          <button
            onClick={onComplete}
            className="px-10 py-4 bg-[#54B0AF] text-white text-lg rounded-full shadow hover:bg-teal-600 font-medium font-gotham transition-colors"
          >
            {finalButtonText}
          </button>
        </div>
      )}
    </div>
  );
}
